import { useState, useEffect, useMemo, type JSX } from 'react'
import {
  KeyRound, Plus, Shield, Layers, CheckCircle2,
} from 'lucide-react'
import { useToast } from '../../../components/system/ToastProvider.js'
import { PermissionForm } from '../../../components/rbac/PermissionForm.js'
import { PermissionTable } from '../../../components/rbac/PermissionTable.js'
import { useRbacApi } from '../../../components/rbac/useRbacApi.js'
import type { Permission, CreatePermissionInput, UpdatePermissionInput } from '../../../components/rbac/types.js'

function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof KeyRound
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}10` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: accent }} />
    </div>
  )
}

export default function PlatformPermissionsPage(): JSX.Element {
  const toast = useToast()
  const { listPermissions, createPermission, updatePermission, deletePermission, loading } = useRbacApi({
    baseUrl: '/platform/rbac',
  })

  const [permissions, setPermissions] = useState<Record<string, Permission[]>>({})
  const [dialogVisible, setDialogVisible] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create')
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null)
  const [formLoading, setFormLoading] = useState(false)

  useEffect(() => {
    loadPermissions()
  }, [])

  const loadPermissions = async () => {
    try {
      const data = await listPermissions()
      setPermissions(data.permissions)
    } catch {
      toast.error('Error', 'Failed to load permissions')
    }
  }

  const flatPermissions = useMemo(() =>
    Object.entries(permissions).flatMap(([domain, perms]) => perms.map((p) => ({ ...p, domain }))),
  [permissions])

  const totalPerms = flatPermissions.length
  const systemPerms = flatPermissions.filter(p => p.isSystem).length
  const customPerms = totalPerms - systemPerms
  const totalDomains = Object.keys(permissions).length

  const openCreateDialog = () => {
    setDialogMode('create')
    setSelectedPermission(null)
    setDialogVisible(true)
  }

  const openViewDialog = (perm: Permission) => {
    setDialogMode('view')
    setSelectedPermission(perm)
    setDialogVisible(true)
  }

  const openEditDialog = (perm: Permission) => {
    setDialogMode('edit')
    setSelectedPermission(perm)
    setDialogVisible(true)
  }

  const handleSave = async (data: CreatePermissionInput | UpdatePermissionInput) => {
    try {
      setFormLoading(true)
      if (dialogMode === 'create') {
        await createPermission(data as CreatePermissionInput)
        toast.success('Success', 'Permission created successfully')
      } else if (selectedPermission) {
        await updatePermission(selectedPermission.id, data as UpdatePermissionInput)
        toast.success('Success', 'Permission updated successfully')
      }
      setDialogVisible(false)
      loadPermissions()
    } catch (err: any) {
      toast.error('Error', err.message || 'Failed to save permission')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async (perm: Permission) => {
    try {
      await deletePermission(perm.id)
      toast.success('Success', 'Permission deleted successfully')
      loadPermissions()
    } catch (err: any) {
      toast.error('Error', err.message || 'Failed to delete permission')
    }
  }

  return (
    <div className="w-full space-y-6 px-4 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Platform Permissions</h1>
          <p className="text-sm text-gray-400 mt-1">Manage platform-level permissions.</p>
        </div>
        <button
          onClick={openCreateDialog}
          className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
        >
          <Plus className="h-4 w-4" />
          New Permission
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Permissions" value={totalPerms} icon={KeyRound} accent="#3454cf" />
        <StatCard label="System" value={systemPerms} icon={Shield} accent="#f6931c" />
        <StatCard label="Custom" value={customPerms} icon={CheckCircle2} accent="#10b981" />
        <StatCard label="Domains" value={totalDomains} icon={Layers} accent="#8b5cf6" />
      </div>

      {/* Table */}
      <PermissionTable
        permissions={permissions}
        loading={loading}
        onCreate={openCreateDialog}
        onEdit={openEditDialog}
        onDelete={handleDelete}
        onView={openViewDialog}
        createPermission="PLATFORM_ROLES_MANAGE"
        editPermission="PLATFORM_ROLES_MANAGE"
        deletePermission="PLATFORM_ROLES_MANAGE"
      />
      <PermissionForm
        visible={dialogVisible}
        mode={dialogMode}
        permission={selectedPermission}
        loading={formLoading}
        onSave={handleSave}
        onCancel={() => setDialogVisible(false)}
      />
    </div>
  )
}
